import { format, Locale, parseISO } from 'date-fns';
import { de, enUS, es, fi, fr, nb, sv } from 'date-fns/locale';
import i18next from 'i18next';

/**
 * Returns a date object from specified date representation.
 * @param arg Date object or a number or string representation of a date.
 */
const resolveDate = (arg: number | string | Date): Date =>
  typeof arg === 'string'
    ? parseISO(arg)
    : Number.isInteger(arg)
      ? new Date(arg as number)
      : (arg as Date);

/**
 * Returns a string representing the specified date with time,
 * or empty string if specified value is falsy.
 * @param arg Date object or a number or string representation of a date.
 */
export const formattedDateTime = (arg: number | string | Date | undefined): string => {
  if (!arg) {
    return '';
  }

  return format(resolveDate(arg), 'Pp', { locale: getDateFnsLocale() });
};

/**
 * Returns a string representing the specified date without time,
 * or empty string if specified value is falsy.
 * @param arg Date object or a number or string representation of a date.
 */
export const formattedDate = (arg: number | string | Date | undefined, locale?: Locale): string => {
  return arg ? format(resolveDate(arg), 'P', { locale: locale ?? getDateFnsLocale() }) : '';
};

/**
 * Returns Date FNS locale object representing the selected i18next language,
 * or defaults to English locale if no language.
 */
export const getDateFnsLocale = () => {
  const locales = new Map<string, Locale>([
    ['en', enUS],
    ['sv', sv],
    ['de', de],
    ['fr', fr],
    ['fi', fi],
    ['es', es],
    ['no', nb],
  ]);

  return locales.has(i18next.language) ? locales.get(i18next.language) : enUS;
};
